import React, { useMemo } from 'react';
import { ImagePlaceholder, PrimaryButton } from '../ui/Primitives.jsx';
import { computeFitnessScore, computeIMC, classifyIMC, metricLabel } from '../../lib/fitnessScore.js';
import { fillTemplate } from '../../lib/helpers.js';

// ============================================================================
// Tela de resultado — Fitness Score + IMC. Diferencial competitivo real: os
// números vêm de computeFitnessScore()/computeIMC(), calculados a partir das
// respostas de verdade do usuário (ver src/lib/fitnessScore.js), não são um
// valor decorativo fixo como a concorrência usa.
// ============================================================================
export default function ResultsScreen({ step, answers, gender, onNext }) {
  const result = useMemo(() => computeFitnessScore(answers), [answers]);
  const imc = useMemo(() => computeIMC(answers.weight_current, answers.height), [answers]);
  const imcClass = classifyIMC(imc);

  return (
    <div className="phone-frame flex flex-col safe-top safe-bottom">
      <div className="relative h-48 -mx-0">
        <ImagePlaceholder
          src={typeof step.backgroundImage === 'object' ? step.backgroundImage[gender] : step.backgroundImage}
          className="absolute inset-0 w-full h-full object-cover"
          label="Imagem de fundo (avatar muscular)"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <div className="px-5 -mt-10 relative flex-1 flex flex-col">
        <h1 className="font-display text-2xl font-extrabold mb-4">{step.headline}</h1>

        <div className="bg-surface rounded-xl2 p-5 mb-4">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-ink-muted">{step.scoreCard.label}</div>
              <div className="text-cyan text-sm font-semibold">
                {fillTemplate(step.scoreCard.percentileTemplate, { percentil: result.percentile })}
              </div>
            </div>
            <div className="text-right">
              <span className="font-display text-4xl font-extrabold text-cyan">{result.fitnessScore}</span>
              <span className="text-ink-muted"> /100</span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {step.scoreCard.metrics.map((m) => (
              <MetricBar key={m.key} label={m.label} value={result[m.key]} />
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-surface-2 text-sm">
            <p>
              •{' '}
              {fillTemplate(step.scoreCard.projectionTemplate, {
                min: result.projectionMin,
                max: result.projectionMax,
              })}
            </p>
            <p className="text-ink-muted mt-1">
              {fillTemplate(step.scoreCard.insightTemplate, { maiorOportunidade: metricLabel(result.weakestMetricKey) })}
            </p>
          </div>
        </div>

        {imc != null && (
          <div className="bg-surface rounded-xl2 p-5 mb-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-ink-muted">{step.imcCard.label}</div>
                <div className="text-cyan text-sm font-semibold">{imcClass?.label}</div>
              </div>
              <span className="font-display text-3xl font-extrabold">{imc}</span>
            </div>
            <ImcGauge index={imcClass?.index ?? 1} ranges={step.imcCard.barRanges} />
            <p className="text-sm text-ink-muted mt-3">• {step.imcCard.projectionTemplate}</p>
          </div>
        )}

        <div className="flex-1" />
        <div className="py-4">
          <PrimaryButton onClick={onNext}>{step.cta}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function MetricBar({ label, value }) {
  const level = value >= 70 ? 'Alto' : value >= 40 ? 'Médio' : 'Baixo';
  return (
    <div className="flex items-center gap-3">
      <span className="w-32 text-sm shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-surface-2 rounded-full overflow-hidden">
        <div className="h-full bg-cyan-gradient" style={{ width: `${value}%` }} />
      </div>
      <span className="w-12 text-right text-sm text-ink-muted">{level}</span>
    </div>
  );
}

function ImcGauge({ index, ranges }) {
  const colors = ['bg-sky-400', 'bg-cyan', 'bg-amber-400', 'bg-danger'];
  return (
    <div>
      <div className="flex h-2 rounded-full overflow-hidden">
        {ranges.map((_, i) => (
          <div key={i} className={`flex-1 ${colors[i]} ${i === index ? 'opacity-100' : 'opacity-30'}`} />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-ink-muted mt-1">
        {ranges.map((r) => (
          <span key={r}>{r}</span>
        ))}
      </div>
    </div>
  );
}
